<template>
  <div>
    <Breadcrumbs
      main-link="welcome"
      title="Məhsullar və Xidmətlərim"
      :main="$t('dashboardVue.dashboard')"
    />
    <div
      class="container-fluid mt-4 px-4"
    >
      <div class="row">
        <div class="col-xl-9">
          <!-- Create New Product Button -->
          <div class="d-flex justify-content-start mb-4">
            <button
              class="btn btn-create-product"
              @click.prevent="$router.push({ name: 'newProductOrServicePage' })"
            >
              <i class="fa fa-plus-circle mr-2"></i> Yeni məhsul və xidmət yarat
            </button>
          </div>

          <!-- Filter Section -->
          <div class="filter-card mb-4">
            <div class="filter-header">
              <h3><i class="fa fa-filter mr-2"></i>Filter</h3>
            </div>
            <div class="filter-body">
              <div class="row">
                <div class="col-md-6 mb-3 mb-md-0">
                  <label class="filter-label">Məhsul/Xidmət Növü</label>
                  <select
                    v-model="filters.type.selectedType"
                    class="form-control custom-select"
                  >
                    <option value="">
                      Məhsullar və Xidmətlər
                    </option>
                    <option value="PRODUCT">
                      Məhsullar
                    </option>
                    <option value="SERVICE">
                      Xidmətlər
                    </option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="filter-label">Status</label>
                  <select
                    v-model="filters.status.selectedStatus"
                    class="form-control custom-select"
                  >
                    <option value="">
                      Status
                    </option>
                    <option value="DRAFT">
                      Qaralamada olanlar (DRAFT)
                    </option>
                    <option value="ACTIVE">
                      Təsdiqlənib paylaşılanlar (ACTIVE)
                    </option>
                    <option value="WAITING">
                      Təsdiq gözləyənlər (WAITING)
                    </option>
                    <option value="RETURNED">
                      Geri qaytarılanlar (RETURNED)
                    </option>
                    <option value="REJECTED">
                      Rədd edilənlər (REJECTED)
                    </option>
                    <option value="HAD_ADVERTISEMENT">
                      Reklam edilənlər (HAD_ADVERTISEMENT)
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading Indicator -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2">Məlumatlar yüklənir...</p>
          </div>

          <!-- Products Grid -->
          <div v-else class="row">
            <div
              v-for="product in filteredProducts"
              :key="product.id"
              class="col-sm-6 col-md-4 col-lg-3 mb-4"
            >
              <div
                class="product-card"
                @click.prevent="$router.push({ name: 'product', params: { id: product.id } })"
              >
                <div class="product-image-container">
                  <img
                    :src="mediaBase + product.coverPhotoUrl"
                    class="product-image"
                    alt="Product"
                    loading="lazy"
                  >
                  <div
                    v-if="product.status === 'ACTIVE'"
                    class="status-badge status-active"
                    title="Active"
                  >
                    <i class="fa fa-check-circle"></i>
                  </div>
                  <div
                    v-else-if="product.status === 'WAITING'"
                    class="status-badge status-waiting"
                    title="Waiting"
                  >
                    <i class="fa fa-clock-o"></i>
                  </div>
                  <div
                    v-else-if="product.status === 'DRAFT'"
                    class="status-badge status-draft"
                    title="Draft"
                  >
                    <i class="fa fa-pencil"></i>
                  </div>
                  <div
                    v-else-if="product.status === 'RETURNED'"
                    class="status-badge status-returned"
                    title="Returned"
                  >
                    <i class="fa fa-undo"></i>
                  </div>
                  <div
                    v-else-if="product.status === 'REJECTED'"
                    class="status-badge status-rejected"
                    title="Rejected"
                  >
                    <i class="fa fa-times-circle"></i>
                  </div>
                </div>
                <div class="product-content">
                  <h5 class="product-title">{{ product.name }}</h5>
                  <div class="product-company">
                    <i class="fa fa-building-o mr-1"></i> {{ product.company.name }}
                  </div>
                  <div
                    v-if="product.hasActiveAdvertisement"
                    class="product-ad-badge"
                  >
                    <i class="fa fa-bullhorn mr-1"></i> Advertisement Active
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No Products Message -->
          <div v-if="!loading && filteredProducts.length === 0" class="no-products-message">
            <i class="fa fa-exclamation-circle fa-3x mb-3"></i>
            <h4>Məhsul tapılmadı</h4>
            <p>Seçilmiş filtrlərə uyğun məhsul və ya xidmət tapılmadı.</p>
          </div>

          <!-- View All Products Button -->
          <div class="text-center mt-4 mb-4">
            <button
              class="btn btn-view-all"
              @click.prevent="$router.push({ name: 'myAllProductsPage' })"
            >
              <i class="fa fa-list mr-2"></i> Bütün məhsulları gör
            </button>
          </div>

          <!-- Company Profile Info -->
          <div class="info-alert">
            <div class="info-alert-icon">
              <i class="fa fa-info-circle fa-2x"></i>
            </div>
            <div class="info-alert-content">
              <h5>Şirkət profili məlumatları</h5>
              <p>
                Şirkət haqqında məlumatlarınızı yeniləməyiniz tövsiyə edilir. Məhsul seçərkən satnalmaçılar müəyyən
                kriteriyalar üzrə axtarış verə bilərlər. Həmin axtarış kriteriyalarında şirkətinizin məhsul və
                xidmətlərinin çıxması üçün bu məlumatların doldurulması tövsiyə edilir.
              </p>
            </div>
          </div>
        </div>

        <!-- Sidebar -->
        <div class="col-xl-3">
          <!-- Tariff Card -->
          <div class="tariff-card mb-4">
            <div class="tariff-card-header">
              <i class="fa fa-star mr-2"></i> Sizin tarifiniz
            </div>
            <div class="tariff-card-body">
              <div class="tariff-name">{{ userPackage.pack.name }}</div>
              <button
                class="btn btn-change-tariff"
                @click.prevent="$router.push({ name: 'subscriptionPage' })"
              >
                <i class="fa fa-exchange mr-2"></i> Tarifi dəyiş
              </button>
              <p class="tariff-info">
                <i class="fa fa-info-circle mr-1"></i> Məhsul və xidmət paylaşım limiti və reklam paylaşım limiti paket tarifinə görə müəyyənləşdirilmişdir
              </p>
            </div>
          </div>

          <!-- YouTube Tutorial -->
          <div class="youtube-tutorial-card">
            <div class="youtube-tutorial-icon">
              <a
                href="https://youtu.be/jR8oEhRcGlc"
                target="_blank"
                title="İstifadəçi təlimatı"
              >
                <i class="fa fa-youtube-play"></i>
              </a>
            </div>
            <div class="youtube-tutorial-text">
              <a
                href="https://youtu.be/jR8oEhRcGlc"
                target="_blank"
                title="İstifadəçi təlimatı"
              >
                İstifadəçi üçün təlimat
              </a>
              <p class="mt-2">Platformadan istifadə qaydaları ilə tanış olun</p>
            </div>
          </div>
        </div>

        <!-- Documents Section -->
        <div class="col-xl-12 mt-4">
          <div class="documents-section">
            <ul class="nav nav-tabs custom-tabs" role="tablist">
              <li class="nav-item" role="presentation">
                <a
                  id="distributor-tab"
                  class="nav-link"
                  :class="{ active: activeTab === 'distributor' }"
                  role="tab"
                  aria-controls="distributor"
                  aria-selected="activeTab === 'distributor'"
                  @click="activeTab = 'distributor'"
                >
                  <i class="fa fa-file-text-o mr-2"></i> Distribyutorluq sənədləri
                </a>
              </li>
              <li class="nav-item" role="presentation">
                <a
                  id="certificates-tab"
                  class="nav-link"
                  :class="{ active: activeTab === 'certificates' }"
                  role="tab"
                  aria-controls="certificates"
                  aria-selected="activeTab === 'certificates'"
                  @click="activeTab = 'certificates'"
                >
                  <i class="fa fa-certificate mr-2"></i> Sertifikat və lisenziyalar
                </a>
              </li>
              <li class="nav-item" role="presentation">
                <a
                  id="contracts-tab"
                  class="nav-link"
                  :class="{ active: activeTab === 'contracts' }"
                  role="tab"
                  aria-controls="contracts"
                  aria-selected="activeTab === 'contracts'"
                  @click="activeTab = 'contracts'"
                >
                  <i class="fa fa-handshake-o mr-2"></i> Oxşar işlərlə bağlı müqavilə və referanslar
                </a>
              </li>
            </ul>

            <div class="tab-content p-4 bg-white border border-top-0 rounded-bottom">
              <!-- Distributor Documents Tab -->
              <div
                id="distributor"
                class="tab-pane fade"
                :class="{ show: activeTab === 'distributor', active: activeTab === 'distributor' }"
                role="tabpanel"
                aria-labelledby="distributor-tab"
              >
                <div v-if="documentsLoading" class="text-center py-4">
                  <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                  </div>
                </div>
                <div v-else-if="documents.distributor.length === 0" class="empty-documents">
                  <i class="fa fa-folder-open-o fa-3x mb-3"></i>
                  <p>No documents available.</p>
                </div>
                <div v-else class="row">
                  <div
                    v-for="(doc) in documents.distributor"
                    :key="doc"
                    class="col-md-3 mb-3"
                  >
                    <div class="document-card">
                      <a
                        target="_blank"
                        :href="mediaBase + doc"
                        class="document-link"
                      >
                        <div class="document-icon">
                          <i class="fa fa-file-text-o fa-2x"></i>
                        </div>
                        <div class="document-name">
                          {{ doc.split('/').pop() }}
                        </div>
                      </a>
                      <button 
                        class="document-delete-btn"
                        @click="deleteDocument('distributor', doc)"
                        title="Delete document"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="mt-3">
                  <button
                    class="btn btn-upload"
                    @click="triggerFileInput('distributorFile')"
                  >
                    <i class="fa fa-upload mr-2"></i> Upload File
                  </button>
                  <input
                    ref="distributorFile"
                    type="file"
                    class="d-none"
                    @change="uploadDocument('distributor')"
                  >
                </div>
                <div v-if="uploadProgress > 0 && uploadProgress < 100" class="upload-progress mt-3">
                  <div class="progress">
                    <div 
                      class="progress-bar progress-bar-striped progress-bar-animated" 
                      role="progressbar" 
                      :style="`width: ${uploadProgress}%`" 
                      :aria-valuenow="uploadProgress" 
                      aria-valuemin="0" 
                      aria-valuemax="100"
                    >
                      {{ uploadProgress }}%
                    </div>
                  </div>
                </div>
              </div>

              <!-- Certificates Tab -->
              <div
                id="certificates"
                class="tab-pane fade"
                :class="{ show: activeTab === 'certificates', active: activeTab === 'certificates' }"
                role="tabpanel"
                aria-labelledby="certificates-tab"
              >
                <div v-if="documentsLoading" class="text-center py-4">
                  <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                  </div>
                </div>
                <div v-else-if="documents.certificates.length === 0" class="empty-documents">
                  <i class="fa fa-folder-open-o fa-3x mb-3"></i>
                  <p>No documents available.</p>
                </div>
                <div v-else class="row">
                  <div
                    v-for="(doc, index) in documents.certificates"
                    :key="index"
                    class="col-md-3 mb-3"
                  >
                    <div class="document-card">
                      <a
                        target="_blank"
                        :href="mediaBase + doc"
                        class="document-link"
                      >
                        <div class="document-icon">
                          <i class="fa fa-certificate fa-2x"></i>
                        </div>
                        <div class="document-name">
                          {{ doc.split('/').pop() }}
                        </div>
                      </a>
                      <button 
                        class="document-delete-btn"
                        @click="deleteDocument('certificates', doc)"
                        title="Delete document"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="mt-3">
                  <button
                    class="btn btn-upload"
                    @click="triggerFileInput('certificatesFile')"
                  >
                    <i class="fa fa-upload mr-2"></i> Upload File
                  </button>
                  <input
                    ref="certificatesFile"
                    type="file"
                    class="d-none"
                    @change="uploadDocument('certificates')"
                  >
                </div>
              </div>

              <!-- Contracts Tab -->
              <div
                id="contracts"
                class="tab-pane fade"
                :class="{ show: activeTab === 'contracts', active: activeTab === 'contracts' }"
                role="tabpanel"
                aria-labelledby="contracts-tab"
              >
                <div v-if="documentsLoading" class="text-center py-4">
                  <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                  </div>
                </div>
                <div v-else-if="documents.contracts.length === 0" class="empty-documents">
                  <i class="fa fa-folder-open-o fa-3x mb-3"></i>
                  <p>No documents available.</p>
                </div>
                <div v-else class="row">
                  <div
                    v-for="(doc, index) in documents.contracts"
                    :key="index"
                    class="col-md-3 mb-3"
                  >
                    <div class="document-card">
                      <a
                        target="_blank"
                        :href="mediaBase + doc"
                        class="document-link"
                      >
                        <div class="document-icon">
                          <i class="fa fa-handshake-o fa-2x"></i>
                        </div>
                        <div class="document-name">
                          {{ doc.split('/').pop() }}
                        </div>
                      </a>
                      <button 
                        class="document-delete-btn"
                        @click="deleteDocument('contracts', doc)"
                        title="Delete document"
                      >
                        <i class="fa fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="mt-3">
                  <button
                    class="btn btn-upload"
                    @click="triggerFileInput('contractsFile')"
                  >
                    <i class="fa fa-upload mr-2"></i> Upload File
                  </button>
                  <input
                    ref="contractsFile"
                    type="file"
                    class="d-none"
                    @change="uploadDocument('contracts')"
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Inquiries Section -->
        <div class="col-xl-12 mt-4">
          <div class="inquiries-card">
            <div class="inquiries-header">
              <i class="fa fa-comments mr-2"></i> {{ $t('productInquiries.title') }}
            </div>
            <div class="inquiries-body">
              <div v-if="inquiriesLoading" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-2">{{ $t('productInquiries.loading') }}</p>
              </div>
              <div v-else-if="inquiries.length === 0" class="empty-inquiries">
                <i class="fa fa-inbox fa-3x mb-3"></i>
                <p>{{ $t('productInquiries.noInquiries') }}</p>
              </div>
              <ul v-else class="inquiries-list">
                <li
                  v-for="(inquiry, index) in displayedInquiries"
                  :key="index"
                  class="inquiry-item"
                >
                  <div class="inquiry-content">
                    <div class="inquiry-product">
                      <img
                        :src="mediaBase + inquiry.coverPhotoUrl"
                        class="inquiry-product-image"
                        alt="Product Image"
                      >
                      <div class="inquiry-product-details">
                        <h5 class="inquiry-product-name">{{ inquiry.productName }}</h5>
                        <p class="inquiry-text">{{ inquiry.inquiryText }}</p>
                        <div class="inquiry-user">
                          <i class="fa fa-user mr-1"></i> {{ inquiry.userName + ' ' + inquiry.userSurname }}
                        </div>
                        <div class="inquiry-time">
                          <i class="fa fa-clock-o mr-1"></i> {{ inquiry.inquiryTime }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="inquiry-actions">
                    <button
                      :class="['btn', inquiry.conversationId > 0 ? 'btn-success' : 'btn-primary']"
                      @click="startConversation(inquiry)"
                    >
                      <i :class="['fa', inquiry.conversationId > 0 ? 'fa-comments' : 'fa-comment']"></i>
                      {{ inquiry.conversationId > 0 ? $t('productInquiries.continueConversation') : $t('productInquiries.startConversation') }}
                    </button>
                  </div>
                </li>
              </ul>
              <div v-if="!inquiriesLoading && inquiries.length > 10 && !showAllInquiries" class="text-center mt-3">
                <button
                  class="btn btn-view-all"
                  @click="showAllInquiries = true"
                >
                  <i class="fa fa-eye mr-2"></i> {{ $t('productInquiries.showAll') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from "vuex";
import {mediaBase} from "@/repositories/Repository";
import {addDocument, deleteDocument, getCompanyDocuments, getProducts} from "@/repositories/ProductDataRepository";
import {upload} from "@/repositories/GeneralDataRepository";
import {getInquiriesForCompany} from "@/repositories/InquiryDataRepository";
import {startConversation} from "@/repositories/ProductConversationDataRepository";

export default {
  data() {
    return {
      mediaBase: mediaBase,
      products: [],
      activeTab: 'distributor',
      documents: {
        distributor: [],
        certificates: [],
        contracts: []
      },
      inquiries: [],
      showAllInquiries: false,
      uploadProgress: 0,
      loading: true,
      documentsLoading: true,
      inquiriesLoading: true,
      error: null,
      filters: {
        status: {
          selectedStatus: ''
        },
        type: {
          selectedType: ''
        }
      }
    };
  },
  computed: {
    ...mapState({
      token: (state) => state.auth.token,
      user: (state) => state.auth.user,
      company: (state) => state.auth.company,
      sessionType: (state) => state.auth.sessionType,
      profile: (state) => state.auth.profile,
      position: (state) => state.auth.position,
      userPackageStatus: (state) => state.auth.userPackageStatus,
      userPackage: (state) => state.auth.userPackage,
    }),
    displayedInquiries() {
      return this.showAllInquiries ? this.inquiries : this.inquiries.slice(0, 10);
    },
    filteredProducts() {
      return this.products.filter(product => {
        const matchesType = !this.filters.type.selectedType || product.type === this.filters.type.selectedType;
        const matchesStatus = !this.filters.status.selectedStatus ||
            product.status === this.filters.status.selectedStatus ||
            (this.filters.status.selectedStatus === 'HAD_ADVERTISEMENT' && product.hasHadAdvertisement);
        return matchesType && matchesStatus;
      });
    }
  },
  mounted() {
    this.getProducts();
    this.getDocuments();
    this.getInquiries();
  },
  methods: {
    getProducts() {
      this.loading = true;
      this.error = null;

      getProducts(this.token)
        .then(data => {
          if (data.code === 200) {
            this.products = data.products;
          } else {
            this.error = `Error ${data.code}: ${data.message}`;
            this.$swal({
              title: `Error ${data.code}`,
              text: data.message,
              icon: 'error',
            });
          }
        })
        .catch(error => {
          this.error = error.message || 'Failed to load products';
          this.$swal({
            title: 'Error',
            text: this.error,
            icon: 'error',
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getDocuments() {
      this.documentsLoading = true;

      getCompanyDocuments(this.token, this.company.id)
        .then(data => {
          if (data.code === 200) {
            this.documents.distributor = data.companyDocumentsDto.distributionDocuments || [];
            this.documents.certificates = data.companyDocumentsDto.certificatesAndLicenses || [];
            this.documents.contracts = data.companyDocumentsDto.contractsAndReferencesForSimilarWorks || [];
          } else {
            this.$swal({
              title: `Error ${data.code}`,
              text: data.message,
              icon: 'error',
            });
          }
        })
        .catch(error => {
          this.$swal({
            title: 'Error',
            text: error.message || 'Failed to load documents',
            icon: 'error',
          });
        })
        .finally(() => {
          this.documentsLoading = false;
        });
    },

    uploadDocument(type) {
      let fileInput;
      if (type === 'distributor') {
        fileInput = this.$refs.distributorFile;
      } else if (type === 'certificates') {
        fileInput = this.$refs.certificatesFile;
      } else if (type === 'contracts') {
        fileInput = this.$refs.contractsFile;
      }

      let currentFile = fileInput.files[0];
      if (!currentFile) return;

      // Reset progress
      this.uploadProgress = 0;

      // Validate file size
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (currentFile.size > maxSize) {
        this.$swal({
          title: 'File too large',
          text: 'Maximum file size is 10MB',
          icon: 'warning',
        });
        return;
      }

      upload(this.token, type, currentFile, (event) => {
        this.uploadProgress = Math.round((100 * event.loaded) / event.total);
      }, true)
        .then(response => {
          if (response.data.code === 200) {
            const url = response.data.path;
            addDocument(this.token, type, url, this.company.id)
              .then(data => {
                if (data.code === 200) {
                  this.documents[type].push(url);
                  this.$swal({
                    title: 'Success',
                    text: 'Document uploaded successfully',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false
                  });
                } else {
                  this.$swal({
                    title: `Error ${data.code}`,
                    text: data.message,
                    icon: 'error',
                  });
                }
              })
              .finally(() => {
                this.uploadProgress = 0;
                fileInput.value = ''; // Reset file input
              });
          } else {
            this.$swal({
              title: `Error ${response.data.code}`,
              text: response.data.message,
              icon: 'error',
            });
            this.uploadProgress = 0;
            fileInput.value = ''; // Reset file input
          }
        })
        .catch(error => {
          this.uploadProgress = 0;
          fileInput.value = ''; // Reset file input
          this.$swal({
            title: 'Error',
            text: this.$t("cldNotUploadFile") + (error.message || error),
            icon: 'error',
          });
        });
    },

    deleteDocument(type, doc) {
      this.$swal({
        title: 'Are you sure?',
        text: 'This document will be permanently deleted',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          deleteDocument(this.token, type, doc, this.company.id)
            .then(data => {
              if (data.code === 200) {
                const index = this.documents[type].indexOf(doc);
                if (index > -1) {
                  this.documents[type].splice(index, 1);
                }
                this.$swal({
                  title: 'Deleted!',
                  text: 'Document has been deleted.',
                  icon: 'success',
                  timer: 1500,
                  showConfirmButton: false
                });
              } else {
                this.$swal({
                  title: `Error ${data.code}`,
                  text: data.message,
                  icon: 'error',
                });
              }
            })
            .catch(error => {
              this.$swal({
                title: 'Error',
                text: error.message || 'Failed to delete document',
                icon: 'error',
              });
            });
        }
      });
    },

    triggerFileInput(refName) {
      this.$refs[refName].click();
    },

    getInquiries() {
      this.inquiriesLoading = true;

      getInquiriesForCompany(this.token)
        .then(data => {
          if (data) {
            this.inquiries = data;
          } else {
            this.inquiries = [];
          }
        })
        .catch(error => {
          this.$swal({
            title: 'Error',
            text: error.message || 'Failed to load inquiries',
            icon: 'error',
          });
          this.inquiries = [];
        })
        .finally(() => {
          this.inquiriesLoading = false;
        });
    },

    startConversation(inquiry) {
      try {
        this.$swal({
          title: 'Starting conversation...',
          allowOutsideClick: false,
          didOpen: () => {
            this.$swal.showLoading();
          }
        });

        startConversation(this.token, inquiry.id)
          .then(data => {
            this.$swal.close();

            if (data.code === 200) {
              const conversationId = data.id;
              this.$router.push({
                name: 'conversation',
                params: {id: conversationId},
                query: {conversation_type: "product"}
              });
            } else {
              this.$swal({
                title: `Error ${data.code}`,
                text: data.message,
                icon: 'error'
              });
            }
          })
          .catch(error => {
            this.$swal({
              title: 'Error',
              text: error.message || 'Failed to start conversation',
              icon: 'error'
            });
          });
      } catch (error) {
        this.$swal({
          title: 'Error',
          text: error.message || 'An unexpected error occurred',
          icon: 'error'
        });
      }
    },

    // Refresh data methods
    refreshData() {
      this.getProducts();
      this.getDocuments();
      this.getInquiries();
    }
  }
};
</script>


<style scoped>
/* General Styles */
.container-fluid {
  max-width: 1800px;
}

/* Create Product Button */
.btn-create-product {
  background: linear-gradient(135deg, #4a90e2, #2c6ed1);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.btn-create-product:hover {
  background: linear-gradient(135deg, #2c6ed1, #1a5cbf);
  transform: translateY(-2px);
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
}

.btn-create-product:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Filter Card */
.filter-card {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
}

.filter-header {
  background-color: #f8f9fa;
  padding: 15px 20px;
  border-bottom: 1px solid #eaeaea;
}

.filter-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.filter-body {
  padding: 20px;
}

.filter-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
}

.custom-select {
  height: 45px;
  border-radius: 6px;
  border: 1px solid #ddd;
  padding: 0 15px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.custom-select:focus {
  border-color: #4a90e2;
  box-shadow: 0 0 0 0.2rem rgba(74, 144, 226, 0.25);
}

/* Product Cards */
.product-card {
  background-color: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 100%;
  cursor: pointer;
  position: relative;
}

.product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
}

.product-image-container {
  position: relative;
  height: 180px;
  overflow: hidden;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.product-card:hover .product-image {
  transform: scale(1.05);
}

.product-content {
  padding: 15px;
}

.product-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
  line-height: 1.4;
  height: 45px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.product-company {
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 10px;
}

.product-ad-badge {
  background-color: #ffc107;
  color: #333;
  padding: 5px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
  margin-top: 5px;
}

.status-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  z-index: 2;
}

.status-active {
  background-color: #28a745;
}

.status-waiting {
  background-color: #ffc107;
}

.status-draft {
  background-color: #6c757d;
}

.status-returned {
  background-color: #fd7e14;
}

.status-rejected {
  background-color: #dc3545;
}

/* No Products Message */
.no-products-message {
  text-align: center;
  padding: 40px 20px;
  background-color: #f8f9fa;
  border-radius: 10px;
  color: #6c757d;
}

/* View All Button */
.btn-view-all {
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-view-all:hover {
  background-color: #5a6268;
  transform: translateY(-2px);
}

/* Info Alert */
.info-alert {
  display: flex;
  background-color: #e9f5fe;
  border-radius: 10px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.info-alert-icon {
  color: #4a90e2;
  margin-right: 20px;
  display: flex;
  align-items: flex-start;
}

.info-alert-content h5 {
  color: #333;
  margin-bottom: 10px;
  font-weight: 600;
}

.info-alert-content p {
  color: #555;
  margin-bottom: 0;
  line-height: 1.6;
}

/* Tariff Card */
.tariff-card {
  background-color: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.tariff-card-header {
  background-color: #f8f9fa;
  padding: 15px 20px;
  border-bottom: 1px solid #eaeaea;
  font-weight: 600;
  color: #333;
  font-size: 16px;
}

.tariff-card-body {
  padding: 20px;
}

.tariff-name {
  background-color: #28a745;
  color: white;
  padding: 10px;
  border-radius: 6px;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 15px;
}

.btn-change-tariff {
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 0;
  font-weight: 500;
  width: 100%;
  margin-bottom: 15px;
  transition: all 0.3s ease;
}

.btn-change-tariff:hover {
  background-color: #2c6ed1;
}

.tariff-info {
  font-size: 14px;
  color: #6c757d;
  text-align: center;
  line-height: 1.5;
}

/* YouTube Tutorial Card */
.youtube-tutorial-card {
  background-color: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 20px;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
}

.youtube-tutorial-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}

.youtube-tutorial-icon {
  margin-right: 15px;
}

.youtube-tutorial-icon i {
  font-size: 40px;
  color: #FF0000;
}

.youtube-tutorial-text {
  flex: 1;
}

.youtube-tutorial-text a {
  color: #333;
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  display: block;
  margin-bottom: 5px;
}

.youtube-tutorial-text p {
  color: #6c757d;
  font-size: 14px;
  margin: 0;
}

/* Documents Section */
.documents-section {
  margin-bottom: 30px;
}

.custom-tabs {
  border-bottom: none;
}

.custom-tabs .nav-item {
  margin-right: 5px;
}

.custom-tabs .nav-link {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  padding: 12px 20px;
  color: #6c757d;
  font-weight: 500;
  transition: all 0.2s ease;
}

.custom-tabs .nav-link:hover {
  background-color: #e9ecef;
}

.custom-tabs .nav-link.active {
  background-color: white;
  color: #4a90e2;
  border-bottom: 1px solid white;
  font-weight: 600;
}

.empty-documents {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.document-card {
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  transition: all 0.3s ease;
  height: 100%;
}

.document-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.document-link {
  display: block;
  padding: 15px;
  text-decoration: none;
  color: #333;
}

.document-icon {
  text-align: center;
  margin-bottom: 10px;
  color: #4a90e2;
}

.document-name {
  font-size: 14px;
  text-align: center;
  word-break: break-word;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  height: 40px;
}

.document-delete-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
}

.document-card:hover .document-delete-btn {
  opacity: 1;
}

.document-delete-btn:hover {
  background-color: rgba(220, 53, 69, 0.2);
}

.btn-upload {
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-upload:hover {
  background-color: #2c6ed1;
}

.upload-progress {
  margin-top: 10px;
}

/* Inquiries Card */
.inquiries-card {
  background-color: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
}

.inquiries-header {
  background-color: #f8f9fa;
  padding: 15px 20px;
  border-bottom: 1px solid #eaeaea;
  font-weight: 600;
  color: #333;
  font-size: 18px;
}

.inquiries-body {
  padding: 20px;
}

.empty-inquiries {
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
}

.inquiries-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.inquiry-item {
  border-bottom: 1px solid #eaeaea;
  padding: 15px 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.inquiry-item:last-child {
  border-bottom: none;
}

.inquiry-content {
  flex: 1;
  margin-right: 20px;
}

.inquiry-product {
  display: flex;
  align-items: flex-start;
}

.inquiry-product-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  margin-right: 15px;
}

.inquiry-product-details {
  flex: 1;
}

.inquiry-product-name {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #333;
}

.inquiry-text {
  font-size: 14px;
  color: #555;
  margin-bottom: 8px;
  line-height: 1.5;
}

.inquiry-user, .inquiry-time {
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 3px;
}

.inquiry-actions {
  display: flex;
  align-items: center;
}

.inquiry-actions .btn {
  white-space: nowrap;
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.inquiry-actions .btn:hover {
  transform: translateY(-2px);
}

.inquiry-actions .btn-primary {
  background-color: #4a90e2;
  border-color: #4a90e2;
}

.inquiry-actions .btn-success {
  background-color: #28a745;
  border-color: #28a745;
}

/* Responsive Adjustments */
@media (max-width: 1199px) {
  .inquiry-item {
    flex-direction: column;
  }

  .inquiry-content {
    margin-right: 0;
    margin-bottom: 15px;
  }
}

@media (max-width: 991px) {
  .product-image-container {
    height: 160px;
  }
}

@media (max-width: 767px) {
  .filter-card, .tariff-card, .youtube-tutorial-card, .inquiries-card, .document-card {
    margin-bottom: 20px;
  }

  .custom-tabs .nav-link {
    padding: 10px 15px;
    font-size: 14px;
  }

  .inquiry-product {
    flex-direction: column;
  }

  .inquiry-product-image {
    margin-right: 0;
    margin-bottom: 10px;
  }
}

@media (max-width: 575px) {
  .btn-create-product, .btn-view-all, .btn-upload {
    width: 100%;
  }

  .info-alert {
    flex-direction: column;
  }

  .info-alert-icon {
    margin-right: 0;
    margin-bottom: 15px;
    justify-content: center;
  }
}
</style>
